/**
 * Global logger för push token-relaterade händelser
 * Börjar logga från appstart och sparar i minnet
 */

export type LogEntry = {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
};

class PushTokenLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 500;
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();
  private isActive = false;
  private originalConsole: Partial<Pick<Console, 'log' | 'error' | 'warn'>> = {};

  constructor() {
    this.addLog('info', '🚀 PushTokenLogger initialiserad vid appstart');
  }

  private addLog(level: LogEntry['level'], message: string) {
    const timestamp = new Date().toLocaleTimeString('sv-SE', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      fractionalSecondDigits: 3 
    });
    
    const entry: LogEntry = { timestamp, level, message };
    this.logs.push(entry);
    
    // Begränsa antal loggar
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    // Notifiera lyssnare
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  start() {
    if (this.isActive) return;
    
    this.addLog('info', '🔍 Push Token Logger aktiverad');
    this.addLog('info', `📱 window.expoPushToken: ${window.expoPushToken ? 'FINNS' : 'FINNS INTE'}`);
    
    try {
      const stored = localStorage.getItem('expoPushToken');
      this.addLog('info', `💾 localStorage expoPushToken: ${stored ? stored.substring(0, 30) + '...' : 'FINNS INTE'}`);
    } catch (e) {
      this.addLog('error', `❌ Kunde inte läsa localStorage: ${e}`);
    }

    // Spara original console methods
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
    };

    // Override console methods
    const interceptConsole = (method: 'log' | 'error' | 'warn', level: LogEntry['level']) => {
      const original = console[method];
      console[method] = (...args: unknown[]) => {
        // Anropa original
        original.apply(console, args);
        
        // Fånga om relaterat till push tokens
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        
        // Skippa långa meddelanden (>500 tecken) som troligen är innehåll, inte loggar
        if (message.length > 500) {
          return;
        }
        
        // Skippa markdown-innehåll
        if (message.includes('##') || message.includes('```') || message.includes('---')) {
          return;
        }
        
        if (
          message.includes('Push') || 
          message.includes('push') ||
          message.includes('Token') ||
          message.includes('token') ||
          message.includes('expoPushToken') ||
          message.includes('usePushTokenRegistration') ||
          message.includes('WebView-injection') ||
          message.includes('[RN-Native]') ||
          message.includes('[Auth]')
        ) {
          this.addLog(level, message);
        }
      };
    };

    interceptConsole('log', 'info');
    interceptConsole('error', 'error');
    interceptConsole('warn', 'warning');

    this.isActive = true;
  }

  stop() {
    if (!this.isActive) return;
    
    // Återställ console
    console.log = this.originalConsole.log ?? console.log;
    console.error = this.originalConsole.error ?? console.error;
    console.warn = this.originalConsole.warn ?? console.warn;
    
    this.isActive = false;
    this.addLog('info', '⏸️ Push Token Logger stoppad');
  }

  subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.add(listener);
    // Skicka nuvarande loggar direkt
    listener([...this.logs]);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clear() {
    this.logs = [];
    this.addLog('info', '🗑️ Loggar rensade');
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  // Lägg till en manuell logg (för testning)
  log(message: string, level: LogEntry['level'] = 'info') {
    this.addLog(level, message);
  }
}

// Singleton instans
export const pushTokenLogger = new PushTokenLogger();

// Expose globally for React Native to access
if (typeof window !== 'undefined') {
  (window as Window & { pushTokenLogger?: PushTokenLogger }).pushTokenLogger = pushTokenLogger;
}
